import { isNil } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import * as wkt from 'wkt';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnFloor, SnFloorPlan } from '@lib/api/entities';
import { GoogleMapMarker } from '@lib/geo/google/map';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnFloorsAPI {
  private _targetDeletedFloor$ = new Subject<SnFloorPlan>();
  private _targetSelectedFloor$ = new Subject<SnFloorPlan>();
  private _targetEditedFloor$ = new Subject<SnFloorPlan>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public setDeleteFloor(floor: SnFloorPlan): void {
    this._targetDeletedFloor$.next(floor);
  }

  public getDeletedFloor(): Observable<SnFloorPlan> {
    return this._targetDeletedFloor$.asObservable();
  }

  public setSelectFloor(floor: SnFloorPlan): void {
    this._targetSelectedFloor$.next(floor);
  }

  public getSelectedFloor(): Observable<SnFloorPlan> {
    return this._targetSelectedFloor$.asObservable();
  }

  public setEditFloor(floor: SnFloorPlan): void {
    this._targetEditedFloor$.next(floor);
  }

  public getEditFloor(): Observable<SnFloorPlan> {
    return this._targetEditedFloor$.asObservable();
  }

  public getFloorList(
    locationId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnFloor>> {
    return this._httpClient.get(`/api/locations/${locationId}/floors/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((floor) => new SnFloor(floor)),
      })),
    );
  }

  public getFloor(locationId: string, floorId: string): Observable<SnFloor> {
    return this._httpClient
      .get(`/api/locations/${locationId}/floors/${floorId}/`)
      .pipe(map((response: any) => new SnFloor(response)));
  }

  public createFloor(locationId: string, data: object): Observable<SnFloor> {
    const formData = this._transformToFormData(data);

    return this._httpClient
      .post(`/api/locations/${locationId}/floors/`, formData)
      .pipe(map((response: any) => new SnFloor(response)));
  }

  public updateFloor(locationId: string, floorId: string, data: object): Observable<SnFloor> {
    const formData = this._transformToFormData(data);

    return this._httpClient
      .patch(`/api/locations/${locationId}/floors/${floorId}/`, formData)
      .pipe(map((response: any) => new SnFloor(response)));
  }

  public deleteFloor(locationId: string, floorId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('locations.floors.confirmations.deleteFloor'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/locations/${locationId}/floors/${floorId}/`)),
        tap({
          next: () => this._toastrService.success(t('locations.floors.success.deleted')),
          error: () => this._toastrService.error(t('locations.floors.errors.deleteFailed')),
        }),
      );
  }

  public createFloorPlan(locationId: string, floorId: string, data: any): Observable<SnFloorPlan> {
    const formData = this._transformToFloorUpload(data);
    return this._httpClient
      .post(`/api/locations/${locationId}/floors/${floorId}/floor_plans/`, formData)
      .pipe(map((response: any) => new SnFloorPlan(response)));
  }

  public deleteFloorPlan(
    locationId: string,
    floorId: string,
    floorPlanId: string,
  ): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('locations.floors.confirmations.deleteFloorPlan'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() =>
          this._httpClient.delete(
            `/api/locations/${locationId}/floors/${floorId}/floor_plans/${floorPlanId}/`,
          ),
        ),
        tap({
          next: () => this._toastrService.success(t('locations.floors.success.floorPlanDeleted')),
          error: () =>
            this._toastrService.error(t('locations.floors.errors.deleteFloorPlanFailed')),
        }),
      );
  }

  public selectFloorPlan(
    locationId: string,
    floorId: string,
    floorPlanId: string,
  ): Observable<Object> {
    return this._httpClient
      .get<SnFloorPlan>(
        `/api/locations/${locationId}/floors/${floorId}/floor_plans/${floorPlanId}/`,
      )
      .pipe(
        tap((floorPlan: SnFloorPlan) => {
          this._targetSelectedFloor$.next(floorPlan);
        }),
      );
  }

  public editFloorPlan(
    locationId: string,
    floorId: string,
    floorPlanId: string,
  ): Observable<Object> {
    return this._httpClient
      .get<SnFloorPlan>(
        `/api/locations/${locationId}/floors/${floorId}/floor_plans/${floorPlanId}/`,
      )
      .pipe(
        tap((floorPlan: SnFloorPlan) => {
          this._targetEditedFloor$.next(floorPlan);
        }),
      );
  }

  public updateFloorPlan(
    locationId: string,
    floorId: string,
    floorPlanId: string,
    data: any,
  ): Observable<SnFloorPlan> {
    const formData = this._transformToFloorUpload(data);
    return this._httpClient
      .patch(`/api/locations/${locationId}/floors/${floorId}/floor_plans/${floorPlanId}/`, formData)
      .pipe(map((response: any) => new SnFloorPlan(response)));
  }

  public bulkUpdateFloors(locationId: string, floors: SnFloor[]): Observable<object> {
    return this._httpClient.post(`/api/locations/${locationId}/floors/reordering/`, { floors });
  }

  public removeClearPointMarker(locationID: string, floorID: string, marker?: any): any {
    this._httpClient
      .delete(
        `/api/locations/${locationID}/floors/${floorID}/rooms/${marker.feature.properties.roomId}/`,
      )
      .pipe(
        tap({
          next: () => this._toastrService.success(t('locations.floors.success.roomClearRemoved')),
          error: () =>
            this._toastrService.error(t('locations.floors.errors.removeRoomClearFailed')),
        }),
      )
      .subscribe(() => {
        marker.remove();
      });
  }

  public addClearPoint(marker: GoogleMapMarker, locationId: string, floorId: string): any {
    const lat = marker.getLonLat().lat;
    const lon = marker.getLonLat().lon;
    const data = {
      lat,
      lon,
    };
    return this._httpClient
      .post(`/api/locations/${locationId}/floors/${floorId}/clear_point/`, data)
      .pipe(
        tap({
          next: () => this._toastrService.success(t('locations.floors.success.roomCleared')),
          error: () => this._toastrService.error(t('locations.floors.errors.clearFailed')),
        }),
      );
  }

  private _transformToFormData(data: object): FormData {
    const formData = new FormData();

    Object.keys(data).forEach((key: string) => {
      if (Array.isArray(data[key])) {
        data[key].forEach((item) => formData.append(key, item));
      } else {
        formData.append(key, isNil(data[key]) ? '' : data[key]);
      }
    });
    return formData;
  }

  private _transformToFloorUpload(data: Object): FormData {
    const formData = new FormData();

    const nePoint = wkt.stringify(data['nePoint']);
    const swPoint = wkt.stringify(data['swPoint']);

    Object.keys(data).forEach((key) => {
      switch (key) {
        case 'nePoint':
          formData.append('nePoint', nePoint);
          break;
        case 'swPoint':
          formData.append('swPoint', swPoint);
          break;
        case 'image':
          formData.append('image', data[key], data[key].name);
          break;
        default:
          formData.append(key, data[key]);
      }
    });

    return formData;
  }
}
